import {
  type ChapterEstimate,
  type ChapterId,
  STABILITIES,
  type Stability,
  TTS_MODELS,
  type TtsModel,
} from "@contract";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { formatMoneyCents } from "../../../../i18n/format";
import { errorCodeOf } from "../../../api/fetcher";
import { AudioProvider } from "../../../components/audio/AudioProvider";
import { Button } from "../../../components/control/Button";
import { GateButton } from "../../../components/control/GateButton";
import { Select } from "../../../components/control/Select";
import { Card } from "../../../components/data/Card";
import { DataTable } from "../../../components/data/DataTable";
import { Banner } from "../../../components/feedback/Banner";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { DateTime } from "../../../components/format/DateTime";
import { Duration } from "../../../components/format/Duration";
import { Money } from "../../../components/format/Money";
import { Num } from "../../../components/format/Num";
import { useInterfaceLocale } from "../../../components/format/useInterfaceLocale";
import { useGuidance } from "../../../components/guidance";
import { WorkspaceActions } from "../../../components/layout/WorkspaceActions";
import { Dialog } from "../../../components/overlay/Dialog";
import { DraftActions } from "../../../drafts/DraftActions";
import { useWorkspaceDraft } from "../../../drafts/useWorkspaceDraft";
import { ChapterListen } from "../audio/ChapterListen";
import { useBookDetail } from "../bookContext";
import {
  type RenderConfirmation,
  renderConfirmation,
  renderIsActive,
  renderSettingsLocked,
} from "./logic";
import { useEstimate, useStartRender } from "./useRender";

export function RenderScreen() {
  const book = useBookDetail();
  return (
    <AudioProvider key={book.id}>
      <RenderContent />
    </AudioProvider>
  );
}

function RenderContent() {
  const { t } = useTranslation(["render", "casting"]);
  const book = useBookDetail();
  const estimate = useEstimate();
  const settingsDraft = useWorkspaceDraft(book.id, "settings", {
    ttsModel: book.ttsModel,
    stability: book.stability,
  });
  const settings = {
    isPending: settingsDraft.applying || !settingsDraft.loaded,
    error: settingsDraft.error,
    mutate: (patch: Partial<typeof settingsDraft.value>) =>
      settingsDraft.setValue((old) => ({ ...old, ...patch })),
  };
  const start = useStartRender();
  const navigate = useNavigate();
  const locale = useInterfaceLocale();
  const [confirmation, setConfirmation] = useState<RenderConfirmation | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const locked = renderSettingsLocked(book);
  const active = renderIsActive(book.render?.status);
  const data = estimate.data;
  // What stops a render is listed beside the button; a blocked click moves
  // the Author to that list instead of leaving a grey button to interpret.
  const guide = useGuidance();
  const blockers = data?.blockers ?? [];
  const blockerReason = blockers
    .map((code) =>
      code === "NOT_ALL_APPROVED"
        ? t("render:blockers.NOT_ALL_APPROVED")
        : t("render:blockers.CASTING_MISSING"),
    )
    .join(" ");
  const review = (chapterId?: ChapterId) => {
    if (!data || book.drafts?.length || settingsDraft.dirty) return;
    setAcknowledged(false);
    setConfirmation(renderConfirmation(data, chapterId));
  };
  const submit = async () => {
    if (!confirmation || (confirmation.reRenderChapterIds.length > 0 && !acknowledged)) return;
    try {
      await start.mutateAsync(confirmation);
      setConfirmation(null);
      void navigate(`/books/${book.id}/render/progress`);
    } catch {
      // Never reuse a refused or uncertain confirmation. Fetch and review a new Estimate.
      setConfirmation(null);
      void estimate.refetch();
    }
  };
  if (!data && estimate.isPending) return <Skeleton variant="text" lines={8} />;
  if (!data)
    return (
      <ErrorState
        code={errorCodeOf(estimate.error)}
        onRetry={() => {
          void estimate.refetch();
        }}
      />
    );
  const amount = formatMoneyCents(data.toRender.money.cents, locale);
  return (
    <div className="flex flex-col gap-4" data-screen="render">
      <ErrorBanner error={start.error ?? settings.error ?? estimate.error} />
      {active ? (
        <Banner
          tone="working"
          action={
            <Link className="text-accent underline" to={`/books/${book.id}/render/progress`}>
              {t("render:viewProgress")}
            </Link>
          }
        >
          {t("render:active")}
        </Banner>
      ) : null}
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <Card
          title={t("render:title")}
          titleAs="h1"
          description={t("render:description")}
          padding="none"
          className="min-w-0"
        >
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              <DataTable<ChapterEstimate>
                rows={data.chapters}
                rowKey={(row) => row.chapterId}
                template="36px minmax(150px,1fr) 80px 76px 80px 120px 120px"
                columns={[
                  {
                    id: "number",
                    header: t("render:number"),
                    cell: (row) => <Num value={row.number} />,
                  },
                  {
                    id: "title",
                    header: t("render:chapter"),
                    className: "whitespace-normal",
                    cell: (row) => (
                      <Link
                        className="hover:text-accent"
                        to={`/books/${book.id}/review/${row.chapterId}`}
                      >
                        {row.title}
                      </Link>
                    ),
                  },
                  {
                    id: "chars",
                    header: t("render:characters"),
                    cell: (row) => <Num value={row.characters} />,
                  },
                  {
                    id: "credits",
                    header: t("render:credits"),
                    cell: (row) => <Num value={row.credits} />,
                  },
                  {
                    id: "money",
                    header: t("render:amount"),
                    cell: (row) => <Money cents={row.money.cents} approx />,
                  },
                  {
                    id: "audio",
                    header: t("render:audio"),
                    className: "whitespace-normal",
                    cell: (row) =>
                      row.audio ? (
                        <span className="flex flex-col">
                          <span className={row.audio.stale ? "text-warn" : "text-ok"}>
                            {row.audio.stale ? t("render:stale") : t("render:current")}
                          </span>
                          <ChapterListen
                            bookId={book.id}
                            chapterId={row.chapterId}
                            version={row.audio.renderedAt}
                          />
                        </span>
                      ) : (
                        <span className="text-ink-3">
                          {row.approved ? t("render:noAudio") : t("render:unapproved")}
                        </span>
                      ),
                  },
                  {
                    id: "action",
                    header: t("render:action"),
                    cell: (row) => (
                      <GateButton
                        variant="quiet"
                        size="sm"
                        gate={{
                          blocked:
                            !active &&
                            (!row.approved || !data.narrator) &&
                            !settings.isPending &&
                            !start.isPending,
                          reason: !row.approved
                            ? t("render:unapproved")
                            : t("render:missingNarrator"),
                        }}
                        onBlocked={guide.proceed}
                        disabled={active || settings.isPending || start.isPending}
                        onClick={() => review(row.chapterId)}
                      >
                        {row.audio ? t("render:renderAgain") : t("render:renderOne")}
                      </GateButton>
                    ),
                  },
                ]}
              />
            </div>
          </div>
          {settingsDraft.dirty ? (
            <DraftActions draft={settingsDraft} />
          ) : (
            <WorkspaceActions>
              <div className="flex flex-col gap-1">
                <p className="label-mono">
                  {t("render:toRender", { count: data.toRender.chapterCount })}
                </p>
                <p className="font-mono text-data">
                  <Num value={data.toRender.characters} /> {t("render:characters")} ·{" "}
                  <Num value={data.toRender.credits} /> {t("render:credits")}
                </p>
                <p className="text-ink-2">
                  <Duration ms={data.toRender.durationMs} approx style="words" /> ·{" "}
                  <Money cents={data.toRender.money.cents} approx />
                </p>
              </div>
              {book.drafts?.length ? (
                <p className="text-warn">{t("errors:UNAPPLIED_CHANGES")}</p>
              ) : null}
              <GateButton
                gate={{
                  blocked:
                    active ||
                    !renderConfirmation(data) ||
                    settings.isPending ||
                    !!book.drafts?.length,
                  reason: book.drafts?.length
                    ? t("errors:UNAPPLIED_CHANGES")
                    : active
                      ? t("render:active")
                      : blockerReason || t("render:viewProgress"),
                }}
                onBlocked={guide.proceed}
                busy={start.isPending}
                onClick={() => review()}
              >
                {t("render:renderBook", { count: data.toRender.chapterCount, amount })}
              </GateButton>
            </WorkspaceActions>
          )}
        </Card>
        <aside className="flex flex-col gap-4">
          {/* data-guidance-target: a blocked render click scrolls the Author to
              this list, which says what is missing and links to the fix. */}
          <div
            tabIndex={-1}
            data-guidance-target={blockers.length > 0 ? "" : undefined}
            className="focus:outline-none"
          >
            <Card label={t("render:ready")}>
              <ul className="flex flex-col gap-3">
                <li>
                  <Link className="text-accent" to={`/books/${book.id}/review`}>
                    {t("render:approved", { count: data.approvedCount, total: data.includedCount })}
                  </Link>
                </li>
                <li>
                  <Link className="text-accent" to={`/books/${book.id}/casting`}>
                    {data.narrator
                      ? t("render:narrator", { name: data.narrator.name })
                      : t("render:missingNarrator")}
                  </Link>
                </li>
                <li>{t("render:terms", { count: data.termCount })}</li>
              </ul>
              {data.blockers.map((blocker) => (
                <p key={blocker} className="mt-3 text-warn">
                  {
                    {
                      NOT_ALL_APPROVED: t("render:blockers.NOT_ALL_APPROVED"),
                      CASTING_MISSING: t("render:blockers.CASTING_MISSING"),
                    }[blocker]
                  }
                </p>
              ))}
            </Card>
          </div>
          <Card label={t("render:settings")}>
            <div className="flex flex-col gap-3">
              <Select
                label={t("render:model")}
                value={settingsDraft.value.ttsModel}
                disabled={locked || settings.isPending}
                options={[
                  { value: TTS_MODELS[0], label: t("casting:modelV3") },
                  { value: TTS_MODELS[1], label: t("casting:modelV2") },
                ]}
                onChange={(event) => settings.mutate({ ttsModel: event.target.value as TtsModel })}
              />
              <Select
                label={t("render:stability")}
                value={settingsDraft.value.stability}
                disabled={locked || settings.isPending}
                options={STABILITIES.map((value) => ({
                  value,
                  label: {
                    creative: t("render:stabilities.creative"),
                    natural: t("render:stabilities.natural"),
                    robust: t("render:stabilities.robust"),
                  }[value],
                }))}
                onChange={(event) =>
                  settings.mutate({ stability: event.target.value as Stability })
                }
              />
              <p className="text-ink-2">
                {locked ? t("render:settingsLocked") : t("render:settingsHint")}
              </p>
            </div>
          </Card>
          <Card label={t("render:quota")} tone={data.quotaSufficient === false ? "warn" : "sunken"}>
            {data.quota ? (
              <>
                <p>
                  <Num value={data.quota.remaining} /> {t("render:creditsRemaining")}
                </p>
                {data.quota.nextResetAt ? (
                  <p className="mt-1 text-ink-2">
                    {t("render:resets")} <DateTime value={data.quota.nextResetAt} />
                  </p>
                ) : null}
              </>
            ) : (
              <p>{t("render:quotaUnknown")}</p>
            )}
            <p className="mt-3 text-ink-2">
              {data.quotaSufficient === false ? t("render:quotaLow") : t("render:quotaHint")}
            </p>
          </Card>
          <p className="px-1 leading-relaxed text-ink-2">{t("render:singleTip")}</p>
        </aside>
      </div>
      <Dialog
        open={confirmation !== null}
        onClose={() => setConfirmation(null)}
        dismissable={!start.isPending}
        title={t("render:confirmTitle")}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              disabled={start.isPending}
              onClick={() => setConfirmation(null)}
            >
              {t("render:cancel")}
            </Button>
            <GateButton
              gate={{
                blocked:
                  confirmation !== null &&
                  confirmation.reRenderChapterIds.length > 0 &&
                  !acknowledged,
                reason: t("render:acknowledgeFirst"),
              }}
              onBlocked={() => {
                guide.proceed();
                document.getElementById("render-acknowledge")?.focus();
              }}
              busy={start.isPending}
              onClick={() => {
                void submit();
              }}
            >
              {t("render:confirmPaid", {
                amount: formatMoneyCents(confirmation?.money.cents, locale),
              })}
            </GateButton>
          </div>
        }
      >
        {confirmation ? (
          <div
            className="flex flex-col gap-3"
            data-estimate-fingerprint={confirmation.estimate.fingerprint}
          >
            <p>{t("render:confirmBody", { count: confirmation.chapterCount })}</p>
            {confirmation.chapter ? (
              <p className="font-serif text-book-sm">{confirmation.chapter.title}</p>
            ) : null}
            <p className="font-mono text-data">
              <Num value={confirmation.characters} /> {t("render:characters")} ·{" "}
              <Num value={confirmation.credits} /> {t("render:credits")} ·{" "}
              <Money cents={confirmation.money.cents} approx />
            </p>
            <p className="text-ink-2">
              {t("render:estimateTime")}{" "}
              <DateTime value={confirmation.estimate.shownAt} style="dateTime" />
            </p>
            {confirmation.reRenderChapterIds.length > 0 ? (
              <>
                <Banner tone="warn">{t("render:rerenderWarning")}</Banner>
                <ul className="list-inside list-disc">
                  {confirmation.estimate.chapters
                    .filter((row) => confirmation.reRenderChapterIds.includes(row.chapterId))
                    .map((row) => (
                      <li key={row.chapterId}>{row.title}</li>
                    ))}
                </ul>
                <label
                  id="render-acknowledge"
                  tabIndex={-1}
                  data-guidance-target={confirmation.reRenderChapterIds.length > 0 ? "" : undefined}
                  className="flex items-start gap-2 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    className="mt-1 accent-accent"
                  />
                  <span>{t("render:acknowledge")}</span>
                </label>
              </>
            ) : null}
            <p className="text-ink-2">{t("render:chargeNote")}</p>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
